import { useEffect, useMemo, type FC } from 'react';
import { Card, Descriptions, Form } from 'components';
import type { MeterModelFormValues } from '../../types';
import { Col, Flex, Row, Skeleton } from 'antd';
import { MeterModelFilesCard } from '../../components/MeterModelFilesCard';
import { JsonEditorField } from '../../components/DetailsEditor';
import { MeterModelCompatibleModulesFilter } from '../../components/Filter';
import { CompatibleMeterModulesTable } from '../../components/Table';
import { useUpdateMeterModel } from 'api/hooks/meter-models/useUpdateModel';
import { useCreateMeterModel } from 'api/hooks/meter-models/useCreateModel';
import type { CreateMeterModelDto, UpdateMeterModelDto } from '@enmon/commons/lib/tools';
import { DriveFolderType } from '@enmon/commons/lib/tools';
import { isNil } from 'ramda';
import { MODEL_FORM_ID } from '../../constants';
import { getModelDescriptionColumns } from '../../utils/getDescriptionsConfig';
import { useModelDetailContext } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/store';
import { useHistory } from 'react-router-dom';
import { useIsFetching } from 'react-query';
import { QUERY_KEYS } from '@/utils/queryKeys';
import { useIsMagicRole } from '@/hooks/useUser';
import type { Paths } from 'type-fest';
import type { FormInstance } from 'antd';
import styled from 'styled-components';

// The details card takes the overview card's height (their row stretches): the editor fills what is left of the
// card instead of setting the height itself; min-height keeps it usable when the two cards stack (below lg)
const DetailsCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-card-body {
    flex: 1;
    min-height: 320px;
  }

  .ant-form-item,
  .ant-form-item .ant-row,
  .ant-form-item-control,
  .ant-form-item-control-input,
  .ant-form-item-control-input-content {
    height: 100%;
    margin-bottom: 0;
  }
`;

interface MeterModelOverviewTabProps {
  form: FormInstance<MeterModelFormValues>;
  setIsFormChanged: (changed: boolean) => void;
}

export const MeterModelOverviewTab: FC<MeterModelOverviewTabProps> = ({ form, setIsFormChanged }) => {
  const { model, modelId } = useModelDetailContext();
  const isLoadingModel = useIsFetching(QUERY_KEYS.meterModels.one(modelId), { exact: true }) > 0;

  const history = useHistory();
  const isMagicRole = useIsMagicRole();

  const isCreateFormMode = !modelId;

  const { mutate: updateModel, isLoading: isUpdatingModel } = useUpdateMeterModel();
  const { mutate: createModel, isLoading: isCreatingModel } = useCreateMeterModel();

  const descriptionData = useMemo(
    () => ({
      ...model,
      manufacturerId: model?.manufacturer?.id ?? null,
    }),
    [model],
  );

  // Load the values once a model version arrives, but never over the user's own edits
  const modelVersion = model ? `${model.id}-${model.updatedAt}` : undefined;
  useEffect(() => {
    if (modelVersion && !form.isFieldsTouched()) form.resetFields();
  }, [form, modelVersion]);

  // Columns config
  const modelDescriptionsColumn = getModelDescriptionColumns();

  // Handlers
  const handleCreateModel = (values: MeterModelFormValues) => {
    const dto = { ...values, manufacturer: values.manufacturerId } as CreateMeterModelDto;

    createModel(dto, {
      onSuccess: responseData => {
        history.replace(`/reseller-data/model-library/model/${responseData.id}`);
      },
    });
  };

  const handleUpdateModel = (values: MeterModelFormValues) => {
    if (!modelId) throw new Error('Model is not defined in update context');

    // Convert undefined to null for DTO compatibility
    const dtoValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, isNil(value) ? null : value]),
    );

    const dto = { ...dtoValues, manufacturer: dtoValues.manufacturerId } as UpdateMeterModelDto;

    updateModel({ id: modelId, values: dto }, { onSuccess: () => setIsFormChanged(false) });
  };

  const isLoading = isLoadingModel || isUpdatingModel || isCreatingModel;

  // Only the first load: a background refetch must not unmount the form under unsaved edits.
  if (isLoadingModel && !model) return <Skeleton active />;

  return (
    <Form<MeterModelFormValues>
      form={form}
      id={MODEL_FORM_ID}
      initialValues={descriptionData}
      onFinish={values => (isCreateFormMode ? handleCreateModel(values) : handleUpdateModel(values))}
      onValuesChange={() => setIsFormChanged(true)}
    >
      <Flex vertical gap={8}>
        <Row align="stretch" gutter={[8, 8]}>
          <Col lg={12} xs={24}>
            <Card style={{ height: '100%' }}>
              <Descriptions
                column={1}
                columns={modelDescriptionsColumn}
                data={descriptionData}
                isEditing={isMagicRole}
                labelWidth={150}
                layout="vertical"
                size="small"
              />
            </Card>
          </Col>
          <Col lg={12} xs={24}>
            <DetailsCard loading={isLoading} title="Meter model details">
              <Form.Item name={'details' satisfies Paths<MeterModelFormValues>}>
                <JsonEditorField height="100%" isMagicRole={isMagicRole} />
              </Form.Item>
            </DetailsCard>
          </Col>
        </Row>

        {!isCreateFormMode && (
          <>
            <Row align="stretch" gutter={[8, 8]}>
              <Col lg={12} xs={24}>
                <MeterModelFilesCard model={model} folderType={DriveFolderType.Photos} />
              </Col>
              <Col lg={12} xs={24}>
                <MeterModelFilesCard model={model} folderType={DriveFolderType.Docs} />
              </Col>
            </Row>

            <Card title="Compatible meter modules">
              <Flex vertical gap={16}>
                <MeterModelCompatibleModulesFilter />

                <CompatibleMeterModulesTable />
              </Flex>
            </Card>
          </>
        )}
      </Flex>
    </Form>
  );
};
